import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { Exam } from '../exam.js';
import { syncTeacherClassFromExam } from '../utils/teacher-classes.js';

export const updateExamDataMutation = registerMutation({
  operationName: 'updateExamDataMutation',
  type: () => Boolean,
  params: [
    { name: 'examId', type: () => String },
    { name: 'newTitle', type: () => String },
    { name: 'newSubject', type: () => String },
    { name: 'newTopic', type: () => String },
    { name: 'newClass', type: () => String },
  ],
  resolve: (examId: string, newTitle: string, newSubject: string, newTopic: string, newClass: string) => async () => {
    if (!newTitle || newTitle.trim().length === 0) {
      throw new Error('Der Titel darf nicht leer sein.');
    }

    const examCollection = await getCollection(Exam);

    const result = await examCollection.updateOne(
      { _id: examId },
      { $set: { title: newTitle, subject: newSubject, topic: newTopic, class: newClass } },
    );

    if (result.matchedCount === 0) {
      throw new Error(`Klausur mit ID ${examId} nicht gefunden.`);
    }

    await syncTeacherClassFromExam(examId, newClass);

    return true;
  },
});

export const updateExamGroupMutation = registerMutation({
  operationName: 'updateExamGroupMutation',
  type: () => Boolean,
  params: [
    { name: 'examId', type: () => String },
    { name: 'newGroup', type: () => String },
  ],

  resolve: (examId: string, newGroup: string) => async () => {
    if (!newGroup || newGroup.trim().length === 0) {
      throw new Error('Die Gruppe darf nicht leer sein.');
    }

    const examCollection = await getCollection(Exam);

    const result = await examCollection.updateOne({ _id: examId }, { $set: { group: newGroup } });

    if (result.matchedCount === 0) {
      throw new Error(`Klausur mit ID ${examId} nicht gefunden.`);
    }

    return true;
  },
});

export const updateExamArrayMutation = registerMutation({
  operationName: 'updateExamArrayMutation',
  type: () => Boolean,
  params: [
    { name: 'examId', type: () => String },
    { name: 'arrayName', type: () => String },
    { name: 'arrayValues', type: () => [String] },
  ],

  resolve: (examId: string, arrayName: string, arrayValues: string[]) => async () => {
    if (!arrayValues || arrayValues.length === 0) {
      throw new Error(`Das Array "${arrayName}" darf nicht leer sein.`);
    }

    const examCollection = await getCollection(Exam);

    if (arrayValues.length === 0) {
      return true; // Keine neuen Werte, aber Update ist technisch erfolgreich
    }
    const updatedArrayValues = [...arrayValues];
    await examCollection.updateOne({ _id: examId }, { $set: { [arrayName]: updatedArrayValues } });

    return true;
  },
});

export const updateExamGradingSchemeMutation = registerMutation({
  operationName: 'updateExamGradingSchemeMutation',
  type: () => Boolean,
  params: [
    { name: 'examId', type: () => String },
    { name: 'gradingSchemeIds', type: () => [String] },
  ],

  resolve: (examId: string, gradingSchemeIds: string[]) => async () => {
    if (!gradingSchemeIds || gradingSchemeIds.length === 0) {
      throw new Error('Die Bewertungsschema darf nicht leer sein.');
    }

    const examCollection = await getCollection(Exam);
    const exam = await examCollection.findOne({ _id: examId });
    const existingGradingSchemeIds = exam?.gradingSchemeIds || [];
    const newIdsToAdd = gradingSchemeIds.filter(id => !existingGradingSchemeIds.includes(id));
    if (newIdsToAdd.length === 0) {
      return true; // Keine neuen IDs, aber Update ist technisch erfolgreich
    }
    const updatedGradingSchemeIds = [...existingGradingSchemeIds, ...newIdsToAdd];
    await examCollection.updateOne({ _id: examId }, { $set: { gradingSchemeIds: updatedGradingSchemeIds } });

    return true;
  },
});

export const updateExamBlankExamMutation = registerMutation({
  operationName: 'updateExamBlankExamMutation',
  type: () => Boolean,
  params: [
    { name: 'examId', type: () => String },
    { name: 'blankExamIds', type: () => [String] },
  ],

  resolve: (examId: string, blankExamIds: string[]) => async () => {
    if (!blankExamIds || blankExamIds.length === 0) {
      throw new Error('Die leere Klausur darf nicht leer sein.');
    }

    const examCollection = await getCollection(Exam);
    const exam = await examCollection.findOne({ _id: examId });
    const existingBlankExamIds = exam?.blankExamIds || [];
    const newIdsToAdd = blankExamIds.filter(id => !existingBlankExamIds.includes(id));
    if (newIdsToAdd.length === 0) {
      return true; // Keine neuen IDs, aber Update ist technisch erfolgreich
    }
    const updatedBlankExamIds = [...existingBlankExamIds, ...newIdsToAdd];
    await examCollection.updateOne({ _id: examId }, { $set: { blankExamIds: updatedBlankExamIds } });

    return true;
  },
});
